import React, { useRef } from 'react'
import { Link } from 'react-router-dom'
import { motion, useInView } from 'framer-motion'
import { useOptimizedScroll } from '../hooks/useOptimizedScroll'
import { useIsMobile } from '../hooks/useMediaQuery'
import { PageMobileFooter } from './layout/PageMobileFooter'
import { cookiePolicyPageFooter } from './layout/footerConfigs'

export const CookiePolicyPage: React.FC = () => {
  const isMobile = useIsMobile()
  const prefersReducedMotion = useRef(
    typeof window !== 'undefined'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false
  )

  React.useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // Use optimized scroll hook to prevent flickering
  const { isScrolled: _isScrolled, scrollProgress } = useOptimizedScroll()

  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })

  return (
    <>
      {/* Navbar is now global - no individual page navbars needed */}


      {/* Main Content Wrapper */}
      <div
        className="min-h-screen relative"
        style={{
          backgroundColor: '#F7F5F5',
          borderBottomLeftRadius: scrollProgress > 0 ? `${Math.min(scrollProgress * 48, 48)}px` : '48px',
          borderBottomRightRadius: scrollProgress > 0 ? `${Math.min(scrollProgress * 48, 48)}px` : '48px',
          boxShadow: scrollProgress > 0
            ? `0 ${20 + scrollProgress * 20}px ${60 + scrollProgress * 40}px rgba(0, 0, 0, ${0.2 + scrollProgress * 0.15})`
            : '0 20px 60px rgba(0, 0, 0, 0.2)',
          overflow: 'hidden',
          zIndex: 10,
          paddingBottom: '100px',
          transition: 'border-radius 0.1s ease-out, box-shadow 0.2s ease-out',
        }}
      >
        {/* Animated Background Gradient Orbs */}
        <motion.div className="absolute -top-40 -left-40 w-96 h-96 rounded-full opacity-20 blur-3xl" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', willChange: prefersReducedMotion.current ? 'auto' : 'transform' }} animate={prefersReducedMotion.current ? {} : { scale: [1, 1.2, 1], x: [0, 50, 0], y: [0, 30, 0] }} transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }} />
        <motion.div className="absolute top-1/2 -right-40 w-96 h-96 rounded-full opacity-20 blur-3xl" style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', willChange: prefersReducedMotion.current ? 'auto' : 'transform' }} animate={prefersReducedMotion.current ? {} : { scale: [1, 1.3, 1], x: [0, -50, 0], y: [0, -30, 0] }} transition={{ duration: 25, repeat: Infinity, ease: 'easeInOut' }} />
        <motion.div className="absolute bottom-20 left-1/3 w-80 h-80 rounded-full opacity-15 blur-3xl" style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', willChange: prefersReducedMotion.current ? 'auto' : 'transform' }} animate={prefersReducedMotion.current ? {} : { scale: [1, 1.15, 1], x: [0, 30, 0], y: [0, -20, 0] }} transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }} />

        <HeroSection prefersReducedMotion={prefersReducedMotion.current} currentDate={currentDate} />
        <ContentSection prefersReducedMotion={prefersReducedMotion.current} />
      </div>

      <FooterSection isMobile={isMobile} />
    </>
  )
}


const HeroSection: React.FC<{ prefersReducedMotion: boolean; currentDate: string }> = ({ prefersReducedMotion, currentDate }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.3 })

  return (
    <section ref={ref} className="relative pt-48 pb-32" style={{ backgroundColor: 'transparent' }}>
      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.h1 className="text-5xl md:text-6xl lg:text-7xl mb-4 leading-tight" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600, letterSpacing: '-0.03em' }} initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}>
          Cookie Policy
        </motion.h1>
        <motion.p className="text-base mb-8" style={{ color: '#999999', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400 }} initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay: 0.2, ease: [0.4, 0.0, 0.2, 1] }}>
          Last updated: {currentDate}
        </motion.p>
        <motion.p className="text-lg md:text-xl max-w-3xl mx-auto leading-relaxed" style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 500 }} initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay: 0.4, ease: [0.4, 0.0, 0.2, 1] }}>
          This Cookie Policy explains how Mernaki uses cookies and similar technologies to improve your experience, analyze traffic, and personalize content across our websites and services.
        </motion.p>
      </div>
    </section>
  )
}

const ContentSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.1 })

  const sections: Array<{
    title: string
    intro?: string
    content?: string[]
    note?: string
    subsections?: Array<{
      subtitle: string
      intro?: string
      content: string[]
      hasBlurred?: boolean
      blurredContent?: string[]
    }>
  }> = [
      {
        title: '1. What Are Cookies?',
        content: [
          'Cookies are small text files stored on your device when you visit our websites',
          'They help us remember your preferences, maintain login sessions, and improve your experience',
          'Cookies enable essential functionality and help us analyze how our services are used'
        ]
      },
      {
        title: '2. Types of Cookies We Use',
        subsections: [
          {
            subtitle: '2.1 Essential Cookies',
            intro: 'Required for the website to function properly:',
            content: [
              'Login and session cookies',
              'Security and CSRF protection cookies',
              'Subdomain routing for our micro-frontend architecture'
            ]
          },
          {
            subtitle: '2.2 Performance & Analytics Cookies',
            intro: 'Used for monitoring and improving performance:',
            content: [
              'Vercel Analytics',
              'Cloudflare Insights',
              'Backend performance monitoring',
              'API usage logs'
            ]
          },
          {
            subtitle: '2.3 Functional Cookies',
            intro: 'Remember your settings and preferences:',
            content: [
              'Language preference',
              'Theme preference (light/dark mode)',
              'UI layout state',
              'Product preview settings'
            ],
            hasBlurred: true,
            blurredContent: ['Project Velocity settings', 'Project Crimson preferences', 'Project Nexus configurations', 'Project Quantum device sync']
          },
          {
            subtitle: '2.4 Marketing / Tracking Cookies',
            content: [
              'We currently do not use marketing or advertising cookies'
            ]
          }
        ]
      },
      {
        title: '3. Third-Party Cookies',
        intro: 'Our services interact with the following third-party platforms that may set their own cookies:',
        subsections: [
          {
            subtitle: '3.1 General Third-Party Services',
            content: [
              'Vercel (hosting and deployment)',
              'Railway (backend APIs)',
              'Cloudflare (security and routing)',
              'Email service providers (contact forms)',
              'Payment processors (Stripe/PayPal)',
              'Database interfaces'
            ]
          },
          {
            subtitle: '3.2 Calendly Integration',
            intro: 'We use Calendly for appointment scheduling, which may set the following cookies:',
            content: [
              'Session cookies to maintain your booking session',
              'Preference cookies to remember your timezone and language settings',
              'Analytics cookies to improve Calendly\'s scheduling interface',
              'Functional cookies to enable calendar integration features'
            ]
          }
        ],
        note: 'We do not control these third-party cookies. For Calendly\'s cookie usage, please refer to Calendly\'s Privacy Policy at https://calendly.com/privacy. For other third-party services, please refer to their respective privacy policies.'
      },
      {
        title: '4. Cookies on Our Subdomains',
        intro: 'Mernaki operates multiple subdomains, each may set different cookies based on functionality:',
        content: [
          'mernaki.de (main website)',
          'hr.mernaki.de (recruitment services)',
          'rare.mernaki.de (rare earth services)',
          'compliance.mernaki.de (compliance tools)',
          'verpackg.mernaki.de (VerpackG services)',
          'raid.mernaki.de (trade intelligence)',
          'recommerce.mernaki.de (5G recommerce)'
        ],
        note: 'All subdomains follow the same Cookie Policy.'
      },
      {
        title: '5. Cookie Consent Management',
        intro: 'We manage cookie consent for all integrated services, including third-party tools:',
        content: [
          'Our website handles cookie consent for all integrated services, including Calendly',
          'By using our booking system, you consent to Calendly\'s necessary cookies for scheduling functionality',
          'You can withdraw consent by clearing your browser cookies or contacting us',
          'Essential cookies for core website functionality cannot be disabled'
        ],
        note: 'We have configured third-party services to respect your privacy preferences where possible.'
      },
      {
        title: '6. How to Manage Cookies',
        content: [
          'You can control cookies through your browser settings',
          'Most browsers allow you to block or delete cookies',
          'You can adjust cookie preferences through our cookie banner',
          'Some Mernaki services may not function properly without essential cookies'
        ],
        note: 'Disabling essential cookies may affect website functionality.'
      },
      {
        title: '7. Changes to This Cookie Policy',
        content: [
          'We may update this Cookie Policy to reflect changes in our technology or legal requirements',
          'Updates will be posted on this page with a new "Last updated" date',
          'We encourage you to review this policy periodically'
        ]
      },
      {
        title: '8. Contact',
        content: [
          'For questions about this Cookie Policy: privacy@mernaki.de',
          'For general support: support@mernaki.de'
        ]
      }
    ]

  return (
    <section ref={ref} className="relative py-16" style={{ backgroundColor: 'transparent' }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {sections.map((section, index) => (
          <motion.div
            key={index}
            className="mb-12"
            initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: index * 0.05, ease: [0.4, 0.0, 0.2, 1] }}
          >
            <h2 className="text-2xl md:text-3xl mb-4" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}>
              {section.title}
            </h2>

            {section.intro && (
              <p className="mb-3" style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontSize: '16px', lineHeight: '1.8' }}>
                {section.intro}
              </p>
            )}

            {section.subsections ? (
              <div className="space-y-6">
                {section.subsections.map((subsection, subIndex) => (
                  <div key={subIndex}>
                    <h3 className="text-lg mb-2" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}>
                      {subsection.subtitle}
                    </h3>
                    {subsection.intro && (
                      <p className="mb-2" style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontSize: '15px', lineHeight: '1.7' }}>
                        {subsection.intro}
                      </p>
                    )}
                    <ul className="space-y-2 ml-6">
                      {subsection.content.map((item, itemIndex) => (
                        <li
                          key={itemIndex}
                          style={{
                            color: '#666666',
                            fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                            fontSize: '16px',
                            lineHeight: '1.8',
                            listStyleType: 'disc'
                          }}
                        >
                          {item}
                        </li>
                      ))}
                      {subsection.hasBlurred && subsection.blurredContent?.map((item, itemIndex) => (
                        <li
                          key={`blurred-${itemIndex}`}
                          style={{
                            color: '#666666',
                            fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                            fontSize: '16px',
                            lineHeight: '1.8',
                            listStyleType: 'disc',
                            filter: 'blur(3px)',
                            userSelect: 'none',
                            pointerEvents: 'none'
                          }}
                        >
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ) : (
              <>
                <ul className="space-y-2 ml-6">
                  {section.content?.map((item, itemIndex) => (
                    <li
                      key={itemIndex}
                      style={{
                        color: '#666666',
                        fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                        fontSize: '16px',
                        lineHeight: '1.8',
                        listStyleType: 'disc'
                      }}
                    >
                      {item}
                    </li>
                  ))}
                </ul>
                {section.note && (
                  <p className="mt-4 p-4 rounded-lg" style={{ backgroundColor: 'rgba(74, 144, 226, 0.08)', color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontSize: '15px', lineHeight: '1.7', border: '1px solid rgba(74, 144, 226, 0.2)' }}>
                    <strong style={{ color: '#1A1A1A' }}>Note:</strong> {section.note}
                  </p>
                )}
              </>
            )}
          </motion.div>
        ))}
      </div>
    </section>
  )
}


const FooterSection: React.FC<{ isMobile: boolean }> = ({ isMobile }) => {
  if (isMobile) {
    return <PageMobileFooter {...cookiePolicyPageFooter} />
  }

  return (
    <footer className="relative" style={{ backgroundColor: '#0E0E0E', position: 'sticky', bottom: 0, zIndex: 1, paddingTop: '140px', paddingBottom: '40px', marginTop: '-80px' }}>
      <div className="h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-8 mb-12">

          {/* Left: Brand */}
          <div className="flex items-center gap-3">
            <img
              src="/Mernaki_Logo_Pack/transparent/mernaki-128.png"
              alt="Mernaki Logo"
              className="object-contain"
              style={{ height: '42px', width: 'auto', opacity: 0.85, filter: 'brightness(0) invert(1)' }}
              loading="lazy"
            />
            <h3
              className="text-lg"
              style={{
                color: '#FFFFFF',
                fontFamily: "'Brush Script MT', 'Lucida Handwriting', 'Apple Chancery', cursive",
                fontWeight: 400
              }}
            >
              Mernaki
            </h3>
          </div>

          {/* Center: Links */}
          <div className="flex items-center gap-6 flex-wrap justify-center">
            <Link
              to="/privacy-policy"
              className="text-sm transition-all duration-300"
              style={{
                color: 'rgba(255, 255, 255, 0.5)',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 400,
                textDecoration: 'none'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
              onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
            >
              Privacy Policy
            </Link>
            <span style={{ color: 'rgba(255, 255, 255, 0.2)' }}>|</span>
            <Link
              to="/terms"
              className="text-sm transition-all duration-300"
              style={{
                color: 'rgba(255, 255, 255, 0.5)',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 400,
                textDecoration: 'none'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
              onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
            >
              Terms of Service
            </Link>
            <span style={{ color: 'rgba(255, 255, 255, 0.2)' }}>|</span>
            <span
              className="text-sm"
              style={{
                color: 'rgba(255, 255, 255, 0.7)',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 500
              }}
            >
              Cookie Policy
            </span>
            <span style={{ color: 'rgba(255, 255, 255, 0.2)' }}>|</span>
            <Link
              to="/impressum"
              className="text-sm transition-all duration-300"
              style={{
                color: 'rgba(255, 255, 255, 0.5)',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 400,
                textDecoration: 'none'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
              onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
            >
              Impressum
            </Link>
          </div>

          {/* Right: Contact */}
          <div className="text-center md:text-right">
            <a
              href="mailto:privacy@mernaki.de"
              className="text-sm transition-all duration-300"
              style={{
                color: 'rgba(255, 255, 255, 0.7)',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 500,
                textDecoration: 'none'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 1)'}
              onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)'}
            >
              privacy@mernaki.de
            </a>
          </div>
        </div>

        {/* Bottom Row */}
        <div
          className="pt-8 text-center"
          style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}
        >
          <p
            className="text-sm"
            style={{
              color: 'rgba(255, 255, 255, 0.3)',
              fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
              fontWeight: 300
            }}
          >
            © {new Date().getFullYear()} Mernaki. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  )
}
